import React, { useState, useEffect } from "react";
import { List, Grid, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "../../assets/css/event.css";
import { eventService } from "../../api/eventService";
import FullScreenLoader from "../../components/Common/Loader";

const DEFAULT_IMAGE = "/assets/images/noImage/no-image3.png";

// Image loader with skeleton
const ImageWithSkeleton = ({
    src,
    alt,
    height = 250,
    width = 250,
    className = "",
}) => {
    const [loaded, setLoaded] = useState(false);
    const [imgSrc, setImgSrc] = useState(src || DEFAULT_IMAGE);
    

    useEffect(() => {
        setLoaded(false);
        if (!src || src.trim() === "") {
            setImgSrc(DEFAULT_IMAGE);
            return;
        }

        const img = new Image();
        img.src = src;
        img.onload = () => setTimeout(() => setLoaded(true), 150);
        img.onerror = () => setImgSrc(DEFAULT_IMAGE);
    }, [src]);

    const isDefaultImage = imgSrc === DEFAULT_IMAGE;

    return (
        <div
            className="image-skeleton-wrapper"
            style={{
                position: "relative",
                maxWidth: "400px",
                width: "100%",
                height: typeof height === "number" ? `${height}px` : height,
                overflow: "hidden",
                background: "#f9f9f9",
                flexShrink: 0,
            }}
        >
            {!loaded && (
                <Skeleton
                    height="100%"
                    width="100%"
                    maxWidth= "400px"
                    baseColor="#e0e0e0"
                    highlightColor="white"
                    style={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 1,
                    }}
                />
            )}

            <img
                src={imgSrc}
                alt={alt}
                loading="lazy"
                className={className}
                style={{
                    width: "100%",
                    height: "100%",
                    maxWidth: isDefaultImage ? "270px" : "100%",
                    objectFit: isDefaultImage ? "contain" : "cover",
                    position: "relative",
                    margin: isDefaultImage ? "0 auto" : "0",
                    top: 0,
                    left: 0,
                    opacity: loaded ? 1 : 0,
                    transition: "opacity 0.5s ease-in-out",
                    zIndex: 2,
                }}
                onLoad={() => setLoaded(true)}
                onError={() => setImgSrc(DEFAULT_IMAGE)}
            />
        </div>
    );
};

const EventsListSection = () => {
    const [view, setView] = useState("list");
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showLoader, setShowLoader] = useState(false);
    const [progress, setProgress] = useState(0);
    const [dataReady, setDataReady] = useState(false);

    useEffect(() => {
        if (!showLoader) return;
        let value = 10;
        setProgress(value);
        const interval = setInterval(() => {
            value = Math.min(value + Math.random() * 15, 90);
            setProgress(value);
        }, 400);
        return () => clearInterval(interval);
    }, [showLoader]);

    useEffect(() => {

        const fetchEvents = async () => {
            setShowLoader(true);
            setDataReady(false);
            try {
                const res = await eventService.getEvents();
                if (res?.ZMESSAGE?.[0]?.RETURNCODE === "0") {
                    console.error("Error fetching events:", res?.ZMESSAGE?.[0]?.MESSAGE);
                    setError(res?.ZMESSAGE?.[0]?.MESSAGE || "Failed to load events.");
                    setEvents([]);
                    return;
                }
                const eventList = res?.Event || [];
                const mappedEvents = eventList.map((e) => {
                    const startDate = new Date(e.EventStartDate);
                    const endDate = new Date(e.EventEndDate);

                    const month = startDate.toLocaleString("en-US", { month: "short" }).toUpperCase();
                    const day = String(startDate.getDate()).padStart(2, "0");
                    const year = startDate.getFullYear(); 

                    const formatFullDate = (date) => date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

                    return {
                        id: e.Id,
                        date: { month, day, year }, 
                        startDate: formatFullDate(startDate),
                        endDate: formatFullDate(endDate),
                        image: e.EventImage || e.MediaBaseURL || "/assets/images/events/default.png",
                        title: e.EventName,
                        address: e.EventPlace,
                        time: `${e.EventStartTime?.slice(0, 5)} – ${e.EventEndTime?.slice(0, 5)}`,
                        description: e.EventShortDescription || e.EventLongDescription,
                        formUrl: e.FormURL || "",
                    };
                });
                setEvents(mappedEvents);
            } catch (err) {
                console.error("Error fetching events:", err);
                setError("Failed to load events. Please try again later.");
            } finally {
                setProgress(100);
                setTimeout(() => {
                    setShowLoader(false);
                    setDataReady(true);
                    setProgress(0);
                }, 400);
            }
        };
        fetchEvents();
    }, []);

    const visibleEvents = events.slice(0);

    return (
        <div className="events-section">
            {showLoader && <FullScreenLoader progress={progress} />}
            <div className="">
                <div className="events-header" data-aos="fade-in">
                    <h6 className="events-title">Upcoming Events</h6>
                    <div className="view-toggle">
                        <div
                            className={`toggle-item ${view === "list" ? "active" : ""}`}
                            onClick={() => setView("list")}
                        >
                            <List size={18} strokeWidth={2} />
                            <span>LIST</span>
                        </div>
                        <div
                            className={`toggle-item ${view === "month" ? "active" : ""}`}
                            onClick={() => setView("month")}
                        >
                            <Grid size={18} strokeWidth={2} />
                            <span>GRID</span>
                        </div>
                    </div>
                </div>

                {loading && <p className="text-center mt-4">Loading events...</p>}
                {error && <p className="text-center text-danger mt-4">{error}</p>}

                {!loading && !error && events.length === 0 && (
                    <div className="no-events-state">
                        <img
                            src="/assets/images/events/noEvent.png"
                            alt="No events available"
                            className="no-events-image"
                        />
                        <h5 className="no-events-title">No Upcoming Events</h5>
                        <p className="no-events-text"> {error} </p>
                    </div>
                )}

                {!loading && !error && view === "list" && (
                    <div className="events-list">
                        {visibleEvents.map((event, idx) => (
                            <div className="event-item" key={event.id || idx}>
                                <div className="event-date">
                                    <span className="month">{event.date.month}</span>
                                    <span className="date-separator"></span>
                                    <span className="day">{event.date.day}</span>
                                    <span className="year">{event.date.year}</span> 

                                </div>
                                <ImageWithSkeleton
                                    src={event.image}
                                    alt={event.title}
                                    height={230}
                                    width="100%"
                                    maxWidth="400px"
                                    className="event-image"
                                />
                                <div className="event-content" data-aos="fade-left">
                                    <h5 className="event-title">{event.title}</h5>
                                    <p className="event-meta">
                                        {event.address} <br />
                                        {event.time} <br />

                                        {event.startDate} – {event.endDate}

                                    </p>
                                    <p className="event-desc">{event.description}</p>
                                    <div className="event-link-separator"></div>
                                    <Link to={event.formUrl || "#"} target="_blank" rel="noopener noreferrer" className="event-link read-more">
                                        BOOK AN APPOINTMENT
                                        <span className="icon">
                                            <ArrowRight size={15} strokeWidth={2} />
                                        </span>
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {!loading && !error && view === "month" && (
                    <div className="events-grid">
                        {visibleEvents.map((event, idx) => (
                            <div className="event-card" data-aos="fade-up" key={event.id || idx}>
                                <div className="event-date">
                                    <span className="month">{event.date.month}</span>
                                    <span className="date-separator"></span>
                                    <span className="day">{event.date.day}</span>
                                </div>
                                <ImageWithSkeleton
                                    src={event.image}
                                    alt={event.title}
                                    height={180}
                                    width="100%"
                                    maxWidth="400px"
                                    className="event-image"
                                />
                                <div>
                                    <h5 className="event-title">{event.title}</h5>
                                    <p className="event-meta">{event.time}</p>
                                    {event.startDate} – {event.endDate}
                                    <Link to={event.formUrl || "#"} target="_blank" rel="noopener noreferrer" className="event-link read-more">
                                        BOOK AN APPOINTMENT
                                        <span className="icon">
                                            <ArrowRight size={15} strokeWidth={2} />
                                        </span>
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default EventsListSection;
